import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';
import { SystemHealthService } from '@core/services/system-health.service';
import { SettingsService } from '@core/services/settings-service';

// a component that does not answer within this time counts as down
export const SYSTEM_STATUS_REQUEST_TIMEOUT = 10000;

/*
  Overall system usability, as shown by the dashboard "system status" card and the
  "System State" indicator in the brand bar. Kept in one place so both always agree.
*/
@Injectable({ providedIn: 'root' })
export class SystemStatusService {

  constructor(
    private systemHealthService: SystemHealthService,
    private settingsService: SettingsService
  ) {
  }

  /*
    Number of errors found (0 = system usable):
      - each configured camera must report its camera and computer status at level 0
        (+2 if a camera is missing from the diagnostics, +6 if the diagnostics are unavailable)
      - the aggregator and dimension server heartbeats must respond (+1 each)
  */
  getErrorsCount(): Observable<number> {
    const cameras$ = this.settingsService.getWorkstationParams().pipe(
      timeout(SYSTEM_STATUS_REQUEST_TIMEOUT),
      map((params: any) => params["/cargoeye/cameras"] || []),
      catchError(() => of(null))
    );
    const devices$ = this.systemHealthService.getHealthStatus().pipe(
      timeout(SYSTEM_STATUS_REQUEST_TIMEOUT),
      map((response: any) => this.getParseStatusResponse(response)['cargoeye-devices']),
      catchError(() => of(null))
    );
    const heartbeatErrors = (heartbeat$: Observable<any>) => heartbeat$.pipe(
      timeout(SYSTEM_STATUS_REQUEST_TIMEOUT),
      map(() => 0),
      catchError(() => of(1))
    );

    return forkJoin([
      cameras$,
      devices$,
      heartbeatErrors(this.systemHealthService.getAggStatus()),
      heartbeatErrors(this.systemHealthService.getDimensionStatus())
    ]).pipe(
      map(([cameras, devices, aggregatorErrors, dimensionErrors]: [any, any, number, number]) => {
        let errorsCount = aggregatorErrors + dimensionErrors;

        if (cameras === null) {
          return errorsCount + 1;    // camera configuration unreadable
        }
        if (devices === null) {
          return errorsCount + 6;    // diagnostics unavailable
        }

        cameras.forEach(camera => {
          const device = devices[camera];
          if (!device) {
            errorsCount += 2;
            return;
          }
          ['camera', 'computer'].forEach(subdevice => {
            const status = device['sub-devices'][subdevice];
            if (!status || status.level != 0) {
              errorsCount += 1;
            }
          });
        });
        return errorsCount;
      }),
      catchError(() => of(1))    // unexpected data counts as an error, never as usable
    );
  }

  getStandardStatusLog(name, level, message) {
    var standardStatus = {};
    standardStatus["name"] = name;
    standardStatus["level"] = level;
    standardStatus["message"] = message;
    return standardStatus;
  }

  getParseStatusResponse(status_response) {
    // response is the response js object from cargoeye diagnostic-aggs api
    // returns the new js object with device level details
    // First, fetch all device ids
    // Matching strings
    var status = status_response['status'];
    var cargoeye_status = {};
    var cloud_fusion = "data_fusion";
    var cge_app = "cge_app";
    var cameras = "cameras";
    cargoeye_status["cargoeye-devices"] = {};
    for (var i = 0; i < status.length; i++) {
      // !!! WARNING: This is a simple naive imperical JSON parser and reorganiser based on the existing knowledge of the ros message pulished; pls update this whole function after the diagnostic_aggs topic changes its format!!!

      // Check name layer first
      var new_topic = status[i]['name'];
      if (new_topic.includes(cge_app)) {
        // This is the cge_app status
        var level = status[i]['level'];
        var message = status[i]['values']
        cargoeye_status["cargoeye-app"] = this.getStandardStatusLog("cargoeye-app", level, message);
      } else if (new_topic.includes(cloud_fusion)) {
        // This is the cloud_fusion status
        var level = status[i]['level'];
        var message = status[i]['values']
        cargoeye_status["cargoeye-services"] = {};
        cargoeye_status["cargoeye-services"]["cloud-fusion"] = this.getStandardStatusLog("cloud-fusion", level, message);
      } else if (new_topic.includes(cameras)) {
        // This is the device status
        var new_device_id = status[i]['hardware_id'];
        var device_ids = cargoeye_status["cargoeye-devices"];
        if (typeof (new_device_id) === 'undefined' || new_device_id === '' || new_device_id === ' ') {
          // Status[0] with overall status
        } else {
          // Parse status categories, camera, computer or manager
          var message = status[i]['name'];
          var subdevice;
          if (message.includes('camera_communication_diagnostics')) {
            subdevice = 'camera';
          } else if (message.includes('computer_status')) {
            subdevice = 'computer';
          } else if (message.includes('manager_diagnostics')) {
            subdevice = 'manager';
          } else if (message.includes('cloud_generat')) {
            subdevice = "cloud-generator";
          } else {
            // TODO: through error back to the API
          }
          var sub_device_status = this.getStandardStatusLog(new_device_id + "-" + subdevice, status[i]['level'], "");

          if (!(new_device_id in device_ids)) {
            // Initialize the device
            device_ids[new_device_id] = this.getStandardStatusLog(new_device_id, 3, ""); // temporarily configured as 3
            device_ids[new_device_id]["sub-devices"] = {};
            device_ids[new_device_id]["sub-devices"][subdevice] = sub_device_status;
          } else {
            // Add onto the device
            device_ids[new_device_id]["sub-devices"][subdevice] = sub_device_status;
          }
        }
      } else {
        // Log error
        console.log("wrong message from ROS topic. Please double check.");
      }
    }
    for (var i = 0; i < Object.keys(cargoeye_status['cargoeye-devices']).length; i++) {
      var device = cargoeye_status['cargoeye-devices'][Object.keys(cargoeye_status['cargoeye-devices'])[i]];
      var ok = true;
      for (let subdevice in device['sub-devices']) {
        if (Object.hasOwnProperty(subdevice)) {
          if (subdevice['level'] !== 0) {
            ok = false;
            break;
          }
        }
      }
      device['level'] = ok ? 0 : 2;
    }
    return cargoeye_status;
  }
}
